import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useNavigate, useParams } from 'react-router-dom';
import BackToList from '../components/BackToList';
import withApi from '../../components/higherOrderComponents/withApi';
import Guid from '../components/Guid';
import Modal from '../components/Modal';
import RelatedLinks from '../components/RelatedLinks';
import StatusPill from '../components/StatusPill';
import { useModal } from '../components/useModal';
import {
  COPENHAGEN_TIME_NOTE,
  formatCopenhagen,
  formatCourtTime
} from '../../common/copenhagenTime';
import { weekdaysReverse } from '../../enums/weekdays';

/**
 * Seasonal court purchase — someone's claim on a recurring slot for a season.
 *
 * Four actions, and the old page guards all four with the same "Are you sure you
 * want to proceed?". They do very different things — one books a season of slots
 * at a venue, one cancels it, one re-runs an integration, one emails the customer
 * — so each says what it does.
 *
 * The confirmation email keeps its two-step shape, which is the best thing about
 * the old page: asking for the preview (`sendConfirmationEmail(id, true)`) shows
 * the mail, and only confirming sends it (`false`). The preview renders in a
 * sandboxed frame inside the dialog.
 *
 * **Cancel shows the day it cancels from.** The endpoint cancels every slot
 * from `cancelSlotsFrom` on — here and at the venue — and keeps the earlier
 * ones. Without the parameter it picks its own default (the subscription's end
 * date, or the first of next month), which used to be invisible: the dialog now
 * opens on that default and lets it be changed.
 *
 * **Change court** moves the purchase to another court from a given day; see
 * `ChangeCourtDialog` below.
 */

const fmtDay = value => formatCopenhagen(value, { dateOnly: true, fallback: '—' });

// Same order as the backend enums.
const SLOT_PURCHASE_STATUS = ['Undefined', 'Purchased', 'Cancelled'];
const SLOT_STATUS = ['Undefined', 'NotIntegrated', 'NotFound', 'NotAvailable', 'Found', 'Reserved', 'CancelledByUser'];
const EXTERNAL_STATUS = ['Undefined', 'Booked', 'Cancelled', 'Pending', 'PendingCancellation'];

// The change-court and cancel endpoints answer a refusal with a plain string.
const errorText = error => (typeof error?.payload?.responseData === 'string'
  ? error.payload.responseData
  : (error?.message || 'unknown error'));

const dayList = dates => (dates || []).map(d => formatCopenhagen(d, { withZone: false })).join(', ');

/**
 * Change court: pick a court (only those at the same facility, in the same price
 * band and on sale are offered, each with the first day it is free at the venue),
 * pick the first day on it (never sooner than two weeks from today, so it cannot
 * collide with pay and play), preview, then move. The move reserves the new court
 * from that day, cancels the old slots from that day here and at the venue, and
 * queues the new slots for booking. The subscription is not touched.
 *
 * Its own dialog rather than a `Modal` spec: it needs a select, a date and a
 * two-step preview, which the shared dialog does not have. Same classes, so it
 * looks the same, and it lives inside `.next-root` like `Modal` does.
 */
const ChangeCourtDialog = ({ api, purchaseId, onClose, onMoved }) => {
  const [options, setOptions] = useState(null);
  const [courtId, setCourtId] = useState('');
  const [moveFrom, setMoveFrom] = useState('');
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let live = true;
    api.seasonalcourts.changeCourtOptions(purchaseId)
      .then(data => { if (live) setOptions(data); })
      .catch(e => { if (live) setError(`Unable to load the courts: ${errorText(e)}`); });
    return () => { live = false; };
  }, [api, purchaseId]);

  useEffect(() => {
    const onKey = event => {
      if (event.key === 'Escape' && !busy) onClose();
    };
    document.addEventListener('keydown', onKey, true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, [busy, onClose]);

  const courts = options?.courts || [];

  const pick = id => {
    const court = courts.find(c => c.id === id);
    setCourtId(id);
    setMoveFrom(court?.firstAvailableDate || options?.earliestMoveDate || '');
    setPreview(null);
    setError(null);
  };

  const run = async dryRun => {
    setBusy(true);
    setError(null);
    try {
      const result = await api.seasonalcourts.changeCourt(purchaseId, courtId, moveFrom, dryRun);
      if (dryRun) {
        setPreview(result);
        setBusy(false);
      } else {
        onMoved(result);
      }
    } catch (e) {
      setPreview(null);
      setError(errorText(e));
      setBusy(false);
    }
  };

  return (
    <div className="next-modal__scrim" role="presentation">
      <div className="next-modal" role="dialog" aria-modal="true" aria-label="Change court">
        <div className="next-modal__head">
          <span className="next-modal__glyph next-modal__glyph--confirm" aria-hidden>⇄</span>
          <div className="next-modal__heading">
            <h2>Change court</h2>
            {options && (
              <span className="next-modal__context">
                Now on {options.currentCourtName} · {weekdaysReverse[options.currentWeekDay]} {formatCourtTime(options.currentTime)}
              </span>
            )}
          </div>
        </div>

        <div className="next-modal__body">
          {!options && !error && <p className="next-modal__text next-muted">Loading the courts…</p>}

          {options && (
            <>
              <p className="next-modal__text">
                Only courts at the same facility, in the same price band and on sale are offered. The move starts
                {' '}{options.minDaysAhead} days from today at the earliest ({options.earliestMoveDate}), so it cannot
                {' '}collide with pay and play. The subscription is not touched.
              </p>

              {courts.length === 0 ? (
                <div className="next-modal__warning">No court can take this purchase right now.</div>
              ) : (
                <>
                  <label className="next-modal__field">
                    <span className="next-modal__label">New court</span>
                    <select className="next-modal__input" value={courtId} onChange={event => pick(event.target.value)}>
                      <option value="">Choose a court…</option>
                      {courts.map(c => (
                        <option key={c.id} value={c.id} disabled={!c.firstAvailableDate}>
                          {`${c.courtName} · ${weekdaysReverse[c.weekDay]} ${formatCourtTime(c.time)} · `}
                          {c.firstAvailableDate
                            ? `free from ${c.firstAvailableDate} (${c.availableSlots} free, ${c.unavailableSlots} taken)`
                            : 'no free slot'}
                        </option>
                      ))}
                    </select>
                  </label>

                  <label className="next-modal__field">
                    <span className="next-modal__label">Move from (Copenhagen day)</span>
                    <input
                      type="date"
                      className="next-modal__input"
                      min={options.earliestMoveDate}
                      value={moveFrom}
                      disabled={!courtId}
                      onChange={event => { setMoveFrom(event.target.value); setPreview(null); }}
                    />
                    <span className="next-modal__hint">
                      Defaults to the first day the chosen court is free at the venue.
                    </span>
                  </label>
                </>
              )}
            </>
          )}

          {preview && (
            <div className="next-modal__text">
              <p>
                <strong>Old court</strong> — {preview.slotsToCancel.length} slot(s) cancelled here and at the venue
                {preview.slotsToCancel.length ? `: ${dayList(preview.slotsToCancel)}` : ''}.
              </p>
              <p>
                <strong>{preview.newCourtName}</strong> — {preview.slotsToBook.length} slot(s) booked
                {preview.slotsToBook.length ? `: ${dayList(preview.slotsToBook)}` : ''}.
              </p>
              {preview.slotsUnavailable.length > 0 && (
                <p>
                  <strong>Not free at the venue, not booked</strong> — {dayList(preview.slotsUnavailable)}.
                </p>
              )}
              <p className="next-muted">{COPENHAGEN_TIME_NOTE}</p>
            </div>
          )}

          {error && <div className="next-modal__warning">{error}</div>}
        </div>

        <div className="next-modal__foot">
          {preview && <span className="next-modal__irreversible">Irreversible action</span>}
          <button type="button" className="next-btn" onClick={onClose} disabled={busy}>Cancel</button>
          {preview ? (
            <button type="button" className="next-btn next-btn--danger-solid" disabled={busy} onClick={() => run(false)}>
              {busy ? '…' : 'Change court'}
            </button>
          ) : (
            <button
              type="button"
              className="next-btn next-btn--primary"
              disabled={busy || !courtId || !moveFrom}
              onClick={() => run(true)}
            >
              {busy ? '…' : 'Preview'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

ChangeCourtDialog.propTypes = {
  api: PropTypes.object.isRequired,
  purchaseId: PropTypes.string.isRequired,
  onClose: PropTypes.func.isRequired,
  onMoved: PropTypes.func.isRequired
};

const SeasonalCourtPurchaseInternal = ({ api }) => {
  const { seasonalCourtPurchaseId } = useParams();
  const navigate = useNavigate();
  const { ask, modalProps } = useModal();
  const [changingCourt, setChangingCourt] = useState(false);

  const [purchase, setPurchase] = useState(null);
  const [failed, setFailed] = useState(false);
  const [note, setNote] = useState(null);
  const [preview, setPreview] = useState(undefined);

  const load = useCallback(async () => {
    try {
      const data = await api.seasonalcourts.getPurchase(seasonalCourtPurchaseId);
      if (!data || data.success === false || !data.id) {
        setFailed(true);
        return;
      }
      setPurchase(data);
      setFailed(false);
    } catch (error) {
      setFailed(true);
    }
  }, [api, seasonalCourtPurchaseId]);

  useEffect(() => {
    load();
  }, [load]);

  if (failed) {
    return (
      <div className="next-page">
        <BackToList to="/next/seasonal-court-purchases">← All seasonal court purchases</BackToList>
        <div className="next-note next-note--error">
          Could not load this purchase.{' '}
          <button type="button" className="next-btn next-btn--quiet" onClick={load}>Retry</button>
        </div>
      </div>
    );
  }

  if (!purchase) return <div className="next-page next-muted">Loading…</div>;

  const ctx = `seasonal court purchase …${String(purchase.id).slice(-6)}`;

  const run = (fn, okText, failText) => async () => {
    try {
      await fn();
      setNote({ tone: 'info', text: okText });
    } catch (error) {
      setNote({ tone: 'error', text: `${failText}: ${error?.message || 'unknown error'}` });
      await load();
      throw error;
    }
    await load();
  };

  const book = () => ask({
    kind: 'confirm',
    title: 'Book this seasonal court?',
    context: `Book · ${ctx}`,
    body: `The slots for the season are booked at the venue for ${purchase.userName || 'this user'}. If the venue cannot take them, the failure comes back on this purchase.`,
    confirmLabel: 'Book',
    onConfirm: run(() => api.seasonalcourts.book(purchase.id), 'Booked.', 'Unable to book')
  });

  const defaultCancelFrom = formatCopenhagen(purchase.defaultCancelSlotsFrom, { dateOnly: true, fallback: '' });

  const cancel = () => ask({
    kind: 'danger',
    title: 'Cancel this seasonal court purchase?',
    context: `Cancel · ${ctx}`,
    body: `${purchase.userName || 'The user'} loses the slots from the day below on — they are cancelled here and at the venue. Earlier slots are kept. The subscription is not touched.`,
    inputType: 'date',
    label: 'Cancel slots from (Copenhagen day)',
    initialValue: defaultCancelFrom,
    hint: `Default ${defaultCancelFrom || 'first of next month'}: the subscription's end date if it has one, otherwise the first of next month.`,
    confirmLabel: 'Cancel purchase',
    onConfirm: value => run(
      () => api.seasonalcourts.cancel(purchase.id, value),
      `Cancelled from ${value}.`,
      'Unable to cancel'
    )()
  });

  const integrate = () => ask({
    kind: 'confirm',
    title: 'Re-integrate this court?',
    context: `Re-integrate · ${ctx}`,
    body: 'The seasonal court is pushed to the integration again. Use it when the venue system and this one disagree about the slots.',
    confirmLabel: 'Re-integrate',
    onConfirm: run(
      () => api.seasonalcourts.integrate(purchase.seasonalCourtId),
      'Sent to the integration.',
      'Unable to re-integrate'
    )
  });

  const sendEmail = async () => {
    setPreview(null);
    ask({
      kind: 'confirm',
      title: 'Send the confirmation email?',
      context: `Confirmation email · ${ctx}`,
      body: `This is what ${purchase.userName || 'the user'} will receive.`,
      confirmLabel: 'Send it',
      onConfirm: run(
        () => api.seasonalcourts.sendConfirmationEmail(purchase.id, false),
        'Confirmation sent.',
        'Unable to send the confirmation'
      )
    });

    try {
      // `true` asks for the preview rather than sending — the old page's flow,
      // and the reason this action is worth two steps.
      const html = await api.seasonalcourts.sendConfirmationEmail(purchase.id, true);
      setPreview(typeof html === 'string' ? html : (html?.data || ''));
    } catch (error) {
      setPreview('<p>The preview could not be loaded. Sending still works.</p>');
    }
  };

  const facts = [
    ['Status', purchase.status
      ? <StatusPill status={purchase.status}>{purchase.status}</StatusPill>
      : <span className="next-muted">—</span>],
    ['User', purchase.userName || <span className="next-muted">—</span>],
    ['User id', purchase.userId
      ? <Guid value={purchase.userId} to={`/next/user/${purchase.userId}`} />
      : <span className="next-muted">—</span>],
    ['Seasonal court id', purchase.seasonalCourtId
      ? <Guid value={purchase.seasonalCourtId} to={`/next/seasonal-court/${purchase.seasonalCourtId}`} />
      : <span className="next-muted">—</span>],
    ['Purchase', purchase.purchaseId
      ? <Guid value={purchase.purchaseId} to={`/next/purchase/${purchase.purchaseId}`} />
      : <span className="next-muted">—</span>],
    ['Subscription', purchase.subscriptionId
      ? <Guid value={purchase.subscriptionId} to={`/next/subscription/${purchase.subscriptionId}`} />
      : <span className="next-muted">—</span>],
    ['Terminated at', fmtDay(purchase.terminatedAt)]
  ];

  const slots = purchase.slots || [];

  return (
    <div className="next-page next-page--wide">
      <BackToList to="/next/seasonal-court-purchases">← All seasonal court purchases</BackToList>

      <header className="next-detail__head">
        <div className="next-detail__title">
          <h1>{purchase.userName || 'Seasonal court purchase'}</h1>
          {purchase.status && <StatusPill status={purchase.status}>{purchase.status}</StatusPill>}
        </div>
        <span className="next-detail__id">{purchase.id}</span>
      </header>

      {note && (
        <div className={`next-note next-note--${note.tone === 'error' ? 'error' : 'info'}`}>
          {note.text}
          <button type="button" className="next-note__close" onClick={() => setNote(null)} aria-label="Dismiss">×</button>
        </div>
      )}

      <div className="next-detail">
        <div className="next-detail__main">
          <section className="next-card">
            <div className="next-card__head"><h2>Purchase</h2></div>
            <div className="next-facts">
              {facts.map(([label, value]) => (
                <div className="next-fact" key={label}>
                  <span className="next-fact__label">{label}</span>
                  <span className="next-fact__value">{value}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head">
              <h2>Slots</h2>
              <span className="next-muted">{slots.length} slot{slots.length === 1 ? '' : 's'} · {COPENHAGEN_TIME_NOTE}</span>
            </div>
            {slots.length === 0 ? (
              <p className="next-table__note next-muted">No slot on this purchase.</p>
            ) : (
              <div className="next-table-wrap">
                <table className="next-table next-table--wide next-table--stack">
                  <thead>
                    <tr>
                      <th>Start</th>
                      <th>End</th>
                      <th>Purchase status</th>
                      <th>Court slot status</th>
                      <th>Venue booking</th>
                      <th>Reference</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slots.map(slot => (
                      <tr key={slot.seasonalCourtSlotPurchaseId}>
                        <td data-label="Start" className="next-nowrap">
                          <Link to={`/next/seasonal-court-slot/${slot.seasonalCourtSlotId}`}>
                            {formatCopenhagen(slot.start)}
                          </Link>
                        </td>
                        <td data-label="End" className="next-nowrap">{formatCopenhagen(slot.end)}</td>
                        <td data-label="Purchase status">
                          <StatusPill status={SLOT_PURCHASE_STATUS[slot.status] || 'Undefined'}>
                            {SLOT_PURCHASE_STATUS[slot.status] || slot.status}
                          </StatusPill>
                        </td>
                        <td data-label="Court slot status">{SLOT_STATUS[slot.slotStatus] || slot.slotStatus}</td>
                        <td data-label="Venue booking">
                          {slot.externalPurchaseId
                            ? (
                              <Link to={`/next/external-purchase/${slot.externalPurchaseId}`}>
                                {EXTERNAL_STATUS[slot.externalStatus] || 'Unknown'}
                              </Link>
                            )
                            : <span className="next-muted">—</span>}
                        </td>
                        <td data-label="Reference">{slot.references || <span className="next-muted">—</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>

        <aside className="next-detail__side">
          <section className="next-card">
            <div className="next-card__head"><h2>Actions</h2></div>
            <div className="next-actionlist">
              <button type="button" className="next-actionlist__item" onClick={book}>Book</button>
              <button type="button" className="next-actionlist__item" onClick={integrate}>Re-integrate</button>
              <button type="button" className="next-actionlist__item" onClick={sendEmail}>
                Send confirmation email
              </button>
              <button type="button" className="next-actionlist__item" onClick={() => setChangingCourt(true)}>
                Change court
              </button>
              <button
                type="button"
                className="next-actionlist__item next-actionlist__item--danger"
                onClick={cancel}
              >
                Cancel purchase
              </button>
            </div>
          </section>

          <section className="next-card">
            <div className="next-card__head"><h2>Links</h2></div>
            <RelatedLinks
              items={[
                { label: 'Emails', hint: 'sent for this purchase', to: `/next/emails?seasonalCourtPurchaseId=${purchase.id}` },
                purchase.userId && { label: 'User', to: `/next/user/${purchase.userId}` },
                purchase.purchaseId && { label: 'Purchase', to: `/next/purchase/${purchase.purchaseId}` },
                purchase.subscriptionId && { label: 'Subscription', to: `/next/subscription/${purchase.subscriptionId}` },
                purchase.seasonalCourtId && { label: 'Seasonal court', to: `/next/seasonal-court/${purchase.seasonalCourtId}` }
              ]}
            />
          </section>
        </aside>
      </div>

      {/* `previewHtml` is only passed while the email dialog is the one open, so
          the other three dialogs stay plain. */}
      <Modal {...modalProps} previewHtml={modalProps.title?.includes('confirmation email') ? preview : undefined} />

      {changingCourt && (
        <ChangeCourtDialog
          api={api}
          purchaseId={purchase.id}
          onClose={() => setChangingCourt(false)}
          onMoved={result => {
            setChangingCourt(false);
            navigate(`/next/seasonal-court-purchase/${result.newSeasonalCourtPurchaseId}`);
          }}
        />
      )}
    </div>
  );
};

SeasonalCourtPurchaseInternal.propTypes = {
  api: PropTypes.object.isRequired
};

const SeasonalCourtPurchase = withApi(SeasonalCourtPurchaseInternal);

SeasonalCourtPurchase.requiresLogin = true;

export default SeasonalCourtPurchase;
